import { Component, inject, input } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FolliaWidgetStoreService } from 'src/app/services/follia-widget/follia-widget-store.service';
import { PromptAI, PromptAIService } from 'src/app/services/promptAI/prompt-ai.service';
import { TipoElemento } from 'src/app/services/utility/utility.service';
import { ElementoForAI } from '../app-follia-widget/chat-data-interface';

/**
 * Componente responsabile della gestione dell'icona interattiva Follia.
 * Gestisce il recupero dei prompt AI relativi a un elemento o una tematica
 * e l'invio dei dati allo store della chat.
 */
@Component({
  selector: 'app-follia-icon-for-element',
  standalone: false,
  templateUrl: './follia-icon-for-element.component.html',
  styleUrl: './follia-icon-for-element.component.scss',
})
export class FolliaIconForElementComponent {
  // Services DI
  private readonly promptAIService = inject(PromptAIService);
  private readonly widgetStore = inject(FolliaWidgetStoreService);

  // --- Signal Inputs  ---

  /** L'elemento corrente ( es. KPI, AZIONE, ETC...)) */
  readonly elemento = input<ElementoForAI>();

  /** Il tipo di elemento */
  readonly tipoElemento = input<TipoElemento>();

  /** La tematica di riferimento legata all'elemento */
  readonly tematica = input<ElementoForAI>();

  /**
   * Avvia la procedura di interazione con l'IA.
   * Recupera i dati necessari.
   */
  async askToAI(): Promise<void> {
    try {
      await this._setDataForAI();
      this.widgetStore.updateSendingToChat();
    } catch (error) {
      console.error("Errore durante l'inizializzazione della chat AI:", error);
    }
  }

  /**
   * Setto i prompt necessari per la chiamata AI
   */
  private async _setDataForAI(): Promise<void> {
    await Promise.all([this.setPromptTematica(), this.setPromptElemento()]);
  }

  /**
   * Gestisce il recupero e la memorizzazione del prompt relativo alla tematica.
   * Se il prompt è già presente nello store, l'operazione viene saltata.
   */
  private async setPromptTematica(): Promise<void> {
    const tematica = this.tematica();
    if (!tematica?.id) return;

    this.widgetStore.setIdTematica(tematica.id);

    // Evita chiamate ridondanti se il prompt è già presente
    if (this.widgetStore.promptTematica()) return;

    const result = await this.getPromptFromElement(tematica.id, 'TEMATICA');
    if (result) {
      const promptCompleto = `${tematica.titolo} ${tematica.descrizione} ${result.prompt}`.trim();
      this.widgetStore.updatetematichePromptByIdMap(tematica.id, promptCompleto);
    }
  }

  /**
   * Gestisce il recupero e l'aggiornamento del prompt relativo all'elemento.
   */
  private async setPromptElemento(): Promise<void> {
    const elemento = this.elemento();
    const tipo = this.tipoElemento();

    if (!elemento?.id || !tipo) return;

    const { id, titolo, descrizione } = elemento;
    let promptCompleto = `${titolo} ${descrizione}`.trim();

    const result = await this.getPromptFromElement(id, tipo);
    if (result) {
      promptCompleto = `${promptCompleto} ${result.prompt}`.trim();
    }
    this.widgetStore.setPromptElemento(promptCompleto);

    this.widgetStore.setChatViewablePrompt(elemento.titolo || elemento.descrizione);
  }

  /**
   * Recupera il prompt dal dell'elemmneto
   * @param  idElemento - ID univoco dell'entità.
   * @param  tipo - Categoria dell'entità.
   * @returns {Promise<PromptAI | null>} Il prompt recuperato o null in caso di errore/assenza.
   */
  async getPromptFromElement(
    idElemento: string,
    tipo: TipoElemento | undefined,
  ): Promise<PromptAI | null> {
    if (!idElemento || !tipo) return null;

    try {
      const response = await firstValueFrom(
        this.promptAIService.getPromptByIdTipoElem(idElemento, tipo),
      );
      return response || null;
    } catch (error) {
      console.error(`Errore recupero prompt [ID: ${idElemento}, Tipo: ${tipo}]:`, error);
      return null;
    }
  }
}
